import { useEffect, useRef, useState } from 'react';

const PRELOAD_AHEAD = 5;

/**
 * Index-page image preloader that guarantees top-down rendering with no
 * layout shift.
 *
 * Up to PRELOAD_AHEAD images are fetched in parallel via background
 * `new Image()` calls. After each one has fully *decoded* (not just
 * received), its index is added to a Set. The hook reports `visibleEnd`
 * — the highest contiguous loaded index — so the page can render exactly
 * items 0..visibleEnd-1. By the time React mounts an <img> for index N,
 * the bytes are received AND decoded; the new element paints at its final
 * dimensions on first paint. No skeleton, no gray, no jump.
 *
 * @param {Array} items
 * @param {number} eligibleLimit  - max index allowed to start loading
 *   (controls the initial-30 cap + sentinel-driven scroll extras).
 * @returns visibleEnd - count of items that should be rendered right now.
 */
export default function useTopDownPreloader(items, eligibleLimit) {
	const [loadedIndices, setLoadedIndices] = useState(() => new Set());
	// Slots that already have a fetch in flight (or finished). Deduped so
	// effect re-runs on visibleEnd change don't restart loaders.
	const startedRef = useRef(new Set());
	// True between mount and unmount; gates state updates from in-flight
	// async callbacks so we don't update state on a torn-down component.
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	// Reset all preloader state when the underlying list changes (Digital
	// reshuffle, etc.) — fresh fetches for the new items.
	useEffect(() => {
		startedRef.current = new Set();
		setLoadedIndices(new Set());
	}, [items]);

	let visibleEnd = 0;
	while (loadedIndices.has(visibleEnd)) visibleEnd += 1;

	useEffect(() => {
		if (!items || items.length === 0) return;

		const startSlot = i => {
			const item = items[i];
			const url = item?.thumbnail || item?.image;
			const settle = () => {
				if (!mountedRef.current) return;
				setLoadedIndices(prev => {
					if (prev.has(i)) return prev;
					const next = new Set(prev);
					next.add(i);
					return next;
				});
			};
			if (!url) {
				settle();
				return;
			}
			const img = new Image();
			img.onerror = settle;
			img.onload = () => {
				// `decode()` resolves only after the bitmap is fully decoded.
				// This is the difference-maker: the visible <img> mounted
				// later with the same src shares the already-decoded data,
				// so its first paint is at final dimensions (no 0-height
				// → final-height jump).
				if (typeof img.decode === 'function') {
					img.decode().then(settle).catch(settle);
				} else {
					settle();
				}
			};
			img.src = url;
		};

		const cap = Math.min(visibleEnd + PRELOAD_AHEAD, eligibleLimit, items.length);
		for (let i = visibleEnd; i < cap; i += 1) {
			if (loadedIndices.has(i)) continue;
			if (startedRef.current.has(i)) continue;
			startedRef.current.add(i);
			startSlot(i);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items, visibleEnd, eligibleLimit]);

	return visibleEnd;
}
